<template>
  <div class="step-page-sheet">
    <Transition
      mode="out-in"
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <StepLoadingSkeleton
        v-if="isLoading"
        key="loading"
        variant="subscriptions"
      />
      <div v-else key="content" class="p-6 lg:px-8 lg:pt-4 lg:pb-8">
        <!-- Header -->
        <div class="mb-6 md:mt-6">
          <h1 class="text-2xl font-bold text-dark-gray">
            Mi suscripción {{ currentYear + 1 }}
          </h1>
          <p class="text-sm text-gray-500 mt-1">
            Acá ves todo lo que pediste este año.
            <template v-if="canCreateOrEdit">
              Podés agregar más cuando quieras
              <template v-if="closingDate"
                >, hasta el
                <strong>{{ formatDate(closingDate) }}</strong></template
              >.
            </template>
          </p>
        </div>

        <!-- Ver pagos anteriores -->
        <button
          v-if="paidOrders.length > 0"
          type="button"
          class="mb-5 flex items-center gap-1.5 text-sm text-aces hover:underline focus:outline-none"
          @click="showPaymentsModal = true"
        >
          <svg
            class="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Ver mis pagos anteriores
        </button>

        <!-- Empty state -->
        <div
          v-if="!editableOrder && allPaidProducts.length === 0 && !canCreateNew"
          class="mt-8 flex flex-col items-center gap-3 py-14 text-center"
        >
          <div
            class="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-2xl"
          >
            📦
          </div>
          <p class="font-semibold text-dark-gray">Todavía no hay pedidos</p>
          <p class="max-w-xs text-sm text-gray-400">
            No tenés productos en tu suscripción para este período.
          </p>
        </div>

        <!-- Falta pagar -->
        <div v-if="editableOrderProducts.length > 0" class="mb-6">
          <h2
            class="flex items-center gap-2 text-base font-semibold text-amber-700 mb-3"
          >
            <span>⏳</span> Falta pagar
          </h2>
          <div
            class="border border-amber-200 bg-amber-50 rounded-lg overflow-hidden"
          >
            <div
              v-for="(prod, index) in editableOrderProducts"
              :key="prod.productId"
              :class="[
                'flex items-center',
                index < editableOrderProducts.length - 1
                  ? 'border-b border-amber-100'
                  : '',
              ]"
            >
              <img
                v-if="prod.image"
                :src="prod.image"
                loading="lazy"
                class="h-16 w-16 flex-shrink-0 object-contain p-1"
              />
              <div v-else class="h-16 w-16 flex-shrink-0" />
              <div class="min-w-0 flex-1 py-3 pr-4">
                <p class="font-semibold text-dark-gray">
                  {{ prod.ecommerceName }}
                </p>
                <p class="text-sm text-gray-500">
                  {{ prod.quantity }} unidades · ${{
                    prod.unitPrice.toLocaleString()
                  }}
                  cada una
                </p>
              </div>
              <p class="font-semibold text-dark-gray flex-shrink-0 pr-4">
                ${{ prod.amount.toLocaleString() }}
              </p>
            </div>
            <div
              class="flex justify-between items-center px-4 py-3 bg-amber-100 border-t border-amber-200"
            >
              <span class="font-semibold text-dark-gray">Total a pagar</span>
              <span class="font-bold text-dark-gray">
                ${{ (editableOrder.total ?? 0).toLocaleString() }}
              </span>
            </div>
            <div class="flex gap-3 p-4 bg-amber-50">
              <button
                type="button"
                class="flex-1 py-2 border border-amber-500 text-amber-700 rounded-lg font-medium focus:outline-none"
                @click="onEdit"
              >
                Editar
              </button>
              <button
                type="button"
                class="flex-1 py-2 bg-aces text-white rounded-lg font-semibold focus:outline-none"
                @click="onPay"
              >
                Pagar ahora
              </button>
            </div>
          </div>
        </div>

        <!-- Ya pagado -->
        <div v-if="allPaidProducts.length > 0">
          <h2
            class="flex items-center gap-2 text-base font-semibold text-green-700 mb-3"
          >
            <span>✓</span> Ya pagado
          </h2>
          <div
            class="border border-light-gray rounded-lg overflow-hidden bg-white"
          >
            <div
              v-for="(item, index) in allPaidProducts"
              :key="`paid-${item.productId}`"
              :class="[
                'flex items-center',
                index < allPaidProducts.length - 1
                  ? 'border-b border-light-gray'
                  : '',
              ]"
            >
              <img
                v-if="item.image"
                :src="item.image"
                loading="lazy"
                class="h-16 w-16 flex-shrink-0 object-contain p-1"
              />
              <div v-else class="h-16 w-16 flex-shrink-0" />
              <div class="min-w-0 flex-1 py-3 pr-4">
                <p class="font-semibold text-dark-gray">
                  {{ item.ecommerceName }}
                </p>
                <p class="text-sm text-gray-500">
                  {{ item.quantity }} unidades · ${{
                    item.unitPrice.toLocaleString()
                  }}
                  cada una
                </p>
                <div class="flex flex-wrap gap-1 mt-1">
                  <span
                    v-for="(payment, pi) in item.payments"
                    :key="pi"
                    class="inline-block text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full font-medium"
                  >
                    Pagado el {{ formatPaymentDate(payment) }}
                  </span>
                </div>
              </div>
              <p class="font-semibold text-dark-gray flex-shrink-0 pr-4">
                ${{ item.amount.toLocaleString() }}
              </p>
            </div>
          </div>
        </div>

        <!-- Total del año -->
        <div
          v-if="totalYear > 0"
          class="mt-6 flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4"
        >
          <span class="font-semibold text-dark-gray">Total del año</span>
          <span class="text-xl font-bold text-dark-gray"
            >${{ totalYear.toLocaleString() }}</span
          >
        </div>

        <!-- Agregar materiales -->
        <button
          v-if="canCreateOrEdit"
          type="button"
          class="btn-primary mt-4"
          @click="onAddMaterials"
        >
          + Agregar materiales
        </button>

        <!-- Nota informativa -->
        <p
          v-if="allPaidProducts.length > 0 && canCreateOrEdit"
          class="mt-4 text-sm text-gray-500 border-l-4 border-aces pl-3 py-1"
        >
          Cada vez que agregás materiales, pagás solo lo nuevo. Lo que ya
          pagaste queda guardado.
        </p>
      </div>
    </Transition>

    <!-- Modal de pagos anteriores -->
    <TransitionRoot appear :show="showPaymentsModal" as="template">
      <Dialog as="div" class="relative z-50" @close="showPaymentsModal = false">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-black bg-opacity-40" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div
            class="flex min-h-full items-end justify-center sm:items-center sm:p-4"
          >
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div
                class="relative w-full max-w-lg rounded-t-2xl sm:rounded-2xl bg-white shadow-xl"
              >
                <!-- Header -->
                <div
                  class="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100"
                >
                  <DialogTitle as="h3" class="text-lg font-bold text-dark-gray">
                    Mis pagos
                  </DialogTitle>
                  <button
                    type="button"
                    class="rounded-full p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                    @click="showPaymentsModal = false"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <!-- Contenido -->
                <div
                  class="overflow-y-auto max-h-[70vh] px-6 py-4 flex flex-col gap-4"
                >
                  <div
                    v-for="(order, i) in paidOrders"
                    :key="order.orderId"
                    class="rounded-xl border border-gray-200 overflow-hidden"
                  >
                    <!-- Cabecera del pago -->
                    <div
                      class="flex items-center justify-between bg-green-50 px-4 py-2.5 border-b border-green-100"
                    >
                      <span class="text-sm font-semibold text-green-800">
                        Pago {{ paidOrders.length > 1 ? `#${i + 1}` : "" }}
                      </span>
                      <span class="text-sm font-bold text-green-800">
                        ${{ (order.total ?? 0).toLocaleString() }}
                      </span>
                    </div>

                    <!-- Productos del pedido -->
                    <div class="divide-y divide-gray-100">
                      <div
                        v-for="prod in sortBySequence(order.products)"
                        :key="prod.productId"
                        class="flex justify-between items-start px-4 py-2.5"
                      >
                        <div class="min-w-0 flex-1 pr-3">
                          <p class="text-sm font-medium text-dark-gray">
                            {{ prod.ecommerceName }}
                          </p>
                          <p class="text-xs text-gray-500">
                            {{ prod.quantity }} uds · ${{
                              prod.unitPrice.toLocaleString()
                            }}
                            c/u
                          </p>
                        </div>
                        <p
                          class="text-sm font-semibold text-dark-gray flex-shrink-0"
                        >
                          ${{ prod.amount.toLocaleString() }}
                        </p>
                      </div>
                    </div>

                    <!-- Detalles del pago -->
                    <div
                      v-if="order.payment"
                      class="bg-gray-50 border-t border-gray-200 px-4 py-3 flex flex-col gap-2"
                    >
                      <div class="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                        <div>
                          <span class="text-gray-500 text-xs">Tipo</span>
                          <p class="font-medium text-dark-gray">
                            {{ paymentTypes[order.payment.type] ?? "—" }}
                          </p>
                        </div>
                        <div v-if="order.payment.bank">
                          <span class="text-gray-500 text-xs">Banco</span>
                          <p class="font-medium text-dark-gray">
                            {{ order.payment.bank }}
                          </p>
                        </div>
                        <div v-if="order.payment.location">
                          <span class="text-gray-500 text-xs"
                            >Nro. de operación</span
                          >
                          <p class="font-medium text-dark-gray truncate">
                            {{ order.payment.location }}
                          </p>
                        </div>
                        <div v-if="order.payment.paymentDate">
                          <span class="text-gray-500 text-xs">Fecha</span>
                          <p class="font-medium text-dark-gray">
                            {{ formatFullDate(order.payment.paymentDate) }}
                          </p>
                        </div>
                      </div>
                      <!-- Comprobante -->
                      <a
                        v-if="order.payment.fileURL"
                        :href="order.payment.fileURL"
                        target="_blank"
                        rel="noopener"
                        class="flex items-center gap-1.5 text-xs text-aces hover:underline mt-1"
                      >
                        <svg
                          class="w-3.5 h-3.5 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                          />
                        </svg>
                        Ver comprobante
                      </a>
                    </div>
                  </div>
                </div>

                <!-- Footer -->
                <div class="px-6 py-4 border-t border-gray-100">
                  <button
                    type="button"
                    class="w-full py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 focus:outline-none"
                    @click="showPaymentsModal = false"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script>
  import { ref, computed, watch, inject } from "vue";
  import axios from "axios";
  import StepLoadingSkeleton from "./StepLoadingSkeleton.vue";
  import {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay,
    DialogTitle,
  } from "@headlessui/vue";

  export default {
    name: "MiSuscripcion",
    components: {
      StepLoadingSkeleton,
      TransitionRoot,
      TransitionChild,
      Dialog,
      DialogOverlay,
      DialogTitle,
    },
    props: {
      authenticatedUser: { type: Object, default: () => ({}) },
    },
    emits: ["selectOrder"],
    setup(props, { emit }) {
      const isLoading = ref(true);
      const orders = ref([]);
      const canCreateNew = ref(false);
      const closingDate = ref(null);
      const currentYear = new Date().getFullYear();
      const showPaymentsModal = ref(false);
      const globalState = inject("globalState");

      const paymentTypes = {
        0: "Depósito",
        1: "Transferencia",
        2: "Efectivo",
        3: "Mercado Pago",
        4: "Campo",
      };

      const editableOrder = computed(
        () => orders.value.find((o) => o.isEditable) ?? null,
      );

      const sortBySequence = (products) =>
        [...products].sort((a, b) => {
          if (
            a.sequenceVisualization == null &&
            b.sequenceVisualization == null
          )
            return 0;
          if (a.sequenceVisualization == null) return 1;
          if (b.sequenceVisualization == null) return -1;
          return a.sequenceVisualization - b.sequenceVisualization;
        });

      const editableOrderProducts = computed(() =>
        sortBySequence(editableOrder.value?.products ?? []),
      );

      const paidOrders = computed(() =>
        orders.value.filter(
          (o) => !o.isEditable && (o.products?.length ?? 0) > 0,
        ),
      );

      const allPaidProducts = computed(() => {
        const map = new Map();
        for (const order of paidOrders.value) {
          for (const prod of order.products) {
            if (map.has(prod.productId)) {
              const existing = map.get(prod.productId);
              existing.quantity += prod.quantity;
              existing.amount += prod.amount;
              existing.payments.push(order.payment);
            } else {
              map.set(prod.productId, {
                ...prod,
                payments: [order.payment],
              });
            }
          }
        }
        return [...map.values()];
      });

      const canCreateOrEdit = computed(
        () => canCreateNew.value || !!editableOrder.value,
      );

      const totalYear = computed(() =>
        orders.value.reduce(
          (sum, order) =>
            sum +
            (order.products ?? []).reduce((s, p) => s + (p.amount ?? 0), 0),
          0,
        ),
      );

      const formatDate = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toLocaleDateString("es-AR", {
          day: "numeric",
          month: "long",
        });
      };

      const formatPaymentDate = (payment) => {
        if (!payment?.paymentDate) return "";
        const d = new Date(payment.paymentDate);
        return `${String(d.getDate()).padStart(2, "0")}/${String(
          d.getMonth() + 1,
        ).padStart(2, "0")}`;
      };

      const fetchAll = async () => {
        isLoading.value = true;
        try {
          const [statusRes, periodRes] = await Promise.all([
            axios.get(
              `Orders/status?churchClientId=${props.authenticatedUser.churchClientId}&clientId=${props.authenticatedUser.clientId}`,
            ),
            axios.get("Periods/getCurrent"),
          ]);

          canCreateNew.value = statusRes.data.canCreateNew;
          const rawOrders = statusRes.data.orders ?? [];
          closingDate.value =
            periodRes.data.closingDate ?? periodRes.data.ClosingDate ?? null;

          orders.value = rawOrders.map((order) => ({
            ...order,
            products: (order.orderDetails ?? [])
              .filter((d) => d.quantity > 0)
              .map((d) => ({
                ...d,
                ecommerceName: d.product?.name,
                image: d.product?.image,
              })),
          }));
        } catch (err) {
          console.log(err?.response);
        } finally {
          isLoading.value = false;
        }
      };

      watch(
        () => globalState.tokenUpdated,
        (newVal) => {
          if (newVal === true) {
            fetchAll();
            globalState.tokenUpdated = false;
          }
        },
      );

      fetchAll();

      const buildPayload = (order) => ({
        orderId: order.orderId,
        isEditable: order.isEditable,
        lockReason: order.lockReason ?? null,
        total: order.total ?? 0,
      });

      const formatFullDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        return d.toLocaleDateString("es-AR", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      };

      return {
        isLoading,
        canCreateNew,
        closingDate,
        currentYear,
        showPaymentsModal,
        paymentTypes,
        editableOrder,
        editableOrderProducts,
        paidOrders,
        allPaidProducts,
        canCreateOrEdit,
        totalYear,
        formatDate,
        formatPaymentDate,
        formatFullDate,
        sortBySequence,
        onEdit() {
          if (!editableOrder.value) return;
          emit("selectOrder", {
            ...buildPayload(editableOrder.value),
            goToStep: 3,
          });
        },
        onPay() {
          if (!editableOrder.value) return;
          emit("selectOrder", {
            ...buildPayload(editableOrder.value),
            goToStep: 5,
          });
        },
        onAddMaterials() {
          if (editableOrder.value) {
            emit("selectOrder", {
              ...buildPayload(editableOrder.value),
              goToStep: 3,
            });
          } else {
            emit("selectOrder", {
              orderId: null,
              isEditable: true,
              lockReason: null,
              total: 0,
              goToStep: 3,
            });
          }
        },
      };
    },
  };
</script>
